import { useEffect, useId, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { track } from '@/lib/analytics';
import { searchPlaces, type Place } from '@/stores/locations';

/**
 * The one way a place is named anywhere in the app.
 *
 * Four screens share it — the find sheet, an advertiser's own location fields, the admin account
 * editor and the border-town console — because there are fifteen thousand localities and a
 * dropdown is not a way to offer them. It is a box you type into and a short list of what
 * matched.
 *
 * The list renders **inline, below the box**, and is never floated over anything. The find
 * sheet's word suggestions already made this decision and for the same reason: a list that opens
 * over a bottom sheet has nowhere to open to.
 *
 * Results are typed against a request counter rather than an AbortController. Both stop a slow
 * answer overwriting a fast one; the counter also survives the request that resolves after the
 * component stopped caring, which is the case that actually shows up when somebody types quickly.
 */
export default function LocationPicker({
  value,
  onChange,
  tier,
  primary = false,
  secondary = false,
  state,
  label,
  hint,
  feature,
  placeholder = 'Type a suburb, town or postcode',
}: {
  value: Place | null;
  onChange: (place: Place | null) => void;
  /** Offer only regions (1) or only localities (2). Omit for both. */
  tier?: 1 | 2;
  /** Offer only what may be a first place: anything but a CBD. Capitals are included. */
  primary?: boolean;
  /** Offer only what may be a second place: anything but a capital. CBDs are included. */
  secondary?: boolean;
  /** Offer only places in this state. */
  state?: string;
  label: string;
  hint?: string;
  /** Analytics feature name. Every control in here derives its own from it. */
  feature: string;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Place[]>([]);
  const [active, setActive] = useState(0);
  // The term the list on screen answers, and whether asking failed. "No place by that name" is
  // only true once the server has said so for what is in the box now — during the debounce and
  // the request it is not known yet, and flashing it on every keystroke was a lie told twice a word.
  const [answered, setAnswered] = useState<{ term: string; failed: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const request = useRef(0);
  const listId = useId();

  // Typing is debounced because every keystroke is a request otherwise, and the lookup runs
  // against a table of fifteen thousand rows on the reader's rate-limit budget.
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setAnswered(null);
      return;
    }
    const ticket = (request.current += 1);
    const timer = setTimeout(() => {
      void searchPlaces(term, tier, { primary, secondary, state })
        .then((places) => {
          if (request.current !== ticket) return;
          setResults(places);
          setActive(0);
          setAnswered({ term, failed: false });
        })
        .catch(() => {
          if (request.current !== ticket) return;
          setResults([]);
          setAnswered({ term, failed: true });
        });
    }, 180);
    return () => clearTimeout(timer);
  }, [query, tier, primary, secondary, state]);

  function choose(place: Place) {
    onChange(place);
    setQuery('');
    setResults([]);
    setAnswered(null);
  }

  const optionId = (index: number) => `${listId}-option-${index}`;
  const term = query.trim();
  const settled = answered !== null && answered.term === term;

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!results.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + results.length) % results.length);
      return;
    }
    if (event.key === 'Enter') {
      // The picker is used inside forms whose submit button is the primary action, so Enter has
      // to pick the place rather than save the page underneath it.
      event.preventDefault();
      const place = results[active];
      if (place) {
        // A pick by Enter is no click, so the delegated listener would never count it.
        track('interact', `${feature}.pick`);
        choose(place);
      }
      return;
    }
    if (event.key === 'Escape') {
      setResults([]);
    }
  }

  return (
    <div className="ee-group">
      <span className="ee-label" id={`${listId}-label`}>
        {label}
      </span>

      {value ? (
        <p className="ee-place">
          <span className="ee-place__name">{value.name}</span>
          <span className="ee-place__meta">
            {value.state}
            {value.tier === 2 && value.parent_name ? ` · ${value.parent_name}` : ''}
            {value.tier === 1 ? ' · region' : ''}
          </span>
          <button
            type="button"
            className="ee-place__clear"
            onClick={() => {
              onChange(null);
              inputRef.current?.focus();
            }}
            data-ee-feature={`${feature}.clear`}
          >
            Change
          </button>
        </p>
      ) : null}

      <Input
        ref={inputRef}
        type="search"
        autoComplete="off"
        role="combobox"
        aria-expanded={results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={results.length ? optionId(active) : undefined}
        aria-labelledby={`${listId}-label`}
        placeholder={value ? 'Search for a different place' : placeholder}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={onKeyDown}
        data-ee-feature={`${feature}.query`}
      />

      {hint ? <p className="ee-hint">{hint}</p> : null}

      {results.length ? (
        <ul className="ee-places" id={listId} role="listbox" aria-labelledby={`${listId}-label`}>
          {results.map((place, index) => (
            <li key={place.id}>
              <button
                type="button"
                role="option"
                id={optionId(index)}
                aria-selected={index === active}
                className={`ee-places__row${index === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(place)}
                data-ee-feature={`${feature}.pick`}
              >
                <span className="ee-places__name">{place.name}</span>
                <span className="ee-places__state">{place.state}</span>
                <span className="ee-places__meta">
                  {place.tier === 1 ? 'Region' : (place.parent_name ?? 'Suburb')}
                  {place.postcode ? ` · ${place.postcode}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Said aloud as well as shown: the count is how somebody who cannot see the list knows it
          arrived, and the arrow keys are how they walk it. */}
      <p className="ee-hint" role="status" aria-live="polite">
        {term && settled
          ? answered.failed
            ? 'That lookup didn’t work. Check your connection and type again.'
            : results.length
              ? <span className="ee-visually-hidden">
                  {results.length === 1 ? '1 place found' : `${results.length} places found`}
                </span>
              : 'No place by that name.'
          : null}
      </p>
    </div>
  );
}
